import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CircularProgress, FormControlLabel, Grid, Typography, useTheme, List, Stack, IconButton, Button, Chip, Switch } from "@mui/material";
import { Search, SearchIconWrapper, StyledInputBase } from "app/shared/SearchGlobal/style";
import _ from "lodash";
import Div from "@jumbo/shared/Div";
import SearchIcon from "@mui/icons-material/Search";
import CustomPagination from 'app/widgets/Shared/Pagination/CustomPagination';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import TrashIconButton from 'app/shared/widgets/TrashIconButton';
import ConfirmDialog from 'app/shared/widgets/ConfirmDialog';
import StoreTwoToneIcon from '@mui/icons-material/StoreTwoTone';
import Crop32OutlinedIcon from '@mui/icons-material/Crop32Outlined';
import VehicleEditModal from './VehicleEditModal';
import vehicleServices from 'app/services/vehicle-services';
import ControlPointOutlinedIcon from '@mui/icons-material/ControlPointOutlined';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from "notistack";


const UsersIndex = () => {
    // const Swal = useSwalWrapper();
    // const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [itemsList, setItemsList] = useState([])
    const [searchQuery, setSearchQuery] = useState('')
    // Por defecto solo los activos. Es lo que hace usable la lista despues de desactivar los
    // vehiculos duplicados: siguen existiendo, con su historial de viajes intacto, pero no
    // estorban. El switch los vuelve a mostrar cuando hace falta reactivar uno.
    const [showInactive, setShowInactive] = useState(false)
    const [listLoading, setListLoading] = useState(false)
    const [currentRecordId, setCurrentRecordId] = useState(null)
    const [openRecordEditModal, setOpenRecordEditModal] = useState(false)
    const [recordInActivationToggle, setRecordInActivationToggle] = useState(null)
    const [confirmDelete, setConfirmDelete] = useState({ open: false, record: null })
    const [deleting, setDeleting] = useState(false)

    const deleteRecord = () => {
        const record = confirmDelete.record;
        if (!record?.id || deleting) return;
        setDeleting(true);
        vehicleServices.deleteVehicle(record.id)
            .then(() => {
                setItemsList((prev) => _.filter(prev, (v) => v.id !== record.id));
                enqueueSnackbar('Vehiculo eliminado', { variant: 'success' });
            })
            .catch((err) => enqueueSnackbar(
                (err?.response?.data?.errors || ['No fue posible eliminar el vehiculo']).join('. '),
                { variant: 'error' }
            ))
            .finally(() => {
                setDeleting(false);
                setConfirmDelete({ open: false, record: null });
            });
    };

    useEffect(() => {
        if(listLoading){
            loadItems()
        }
    }, [listLoading])

    useEffect(() => {
        setListLoading(true)
    }, [])

    useEffect(() => {
        setCurrentPage(1)
        setListLoading(true)
    }, [showInactive])

    const buildItemsParams = () => {
        let par = []

        if(searchQuery) {
            const campos = [
                'code',
                'transport_unit1_ppu',
                'transport_unit2_ppu',
                'transport_unit1_brand',
                'transport_unit1__model',
                'centre_name',
            ].join('_or_')
            par.push(encodeURI(`q[${campos}_cont]=${searchQuery}`))
        }

        if(!showInactive) {
            par.push(encodeURI(`q[active_eq]=true`))
        }

        par.push(`page=${currentPage}`)
        par.push(`include=centre,transport_unit1,transport_unit2`)
        par.push(`${encodeURIComponent(`q[s]`)}=${encodeURIComponent('code asc')}`)

        par = _.join(par, '&')

        return `?${par}`;
    }

    const loadItems = () => {
        vehicleServices.getVehicles(buildItemsParams())
            .then(resp => {
                setItemsList(resp.data)
                setTotalPages(resp.meta.total_pages)
            })
            .catch(function (error) {})
            .finally(() => {
                setListLoading(false)
            });
    }

    const handleSearchQueryChange = (newVal) => {
        setCurrentPage(1)
        setSearchQuery(newVal)
        setListLoading(true)
    }

    const handleRecordChange = (rec) => {
        setItemsList(_.map(itemsList, (u) => { return u.id == rec.id ? rec : u }))
    }

    const handleRecordAdd = (rec) => {
        setItemsList(_.concat(itemsList, rec))
    }

    const toggleVehicleActivation = (record) => {
        if(!record?.id) return

        setRecordInActivationToggle(record.id)
        vehicleServices.toggleVehicleActivation(record.id, '?include=centre,transport_unit1,transport_unit2')
            .then(resp => {
                handleRecordChange(resp)
                enqueueSnackbar(
                    resp.active === false ? "Vehículo desactivado correctamente" : "Vehículo activado correctamente",
                    { variant: "success" }
                )
            })
            .catch(function (error) {}) // el interceptor global de jwtAuth muestra la causa
            .finally(() => {
                setRecordInActivationToggle(null)
            });
    }

    const debounceSearchQueryChange = _.debounce(handleSearchQueryChange, 450)

    const renderItemsList = () => {
        if(itemsList){
            return (
                <React.Fragment>
                    <VehicleEditModal
                        recordID={currentRecordId}
                        isOpen={openRecordEditModal}
                        onClose={(changed, userOutput) => { setOpenRecordEditModal(false) }}
                        onRecordChange={handleRecordChange}
                        onRecordAdd={handleRecordAdd} />
                    <List disablePadding>
                        {_.map(itemsList, (u, i) => {
                            return (
                                <React.Fragment key={u.id}>
                                    <Card sx={{mb: 2}}>
                                        <CardContent sx={{display: 'flex'}}>
                                            <Grid container spacing={5}>
                                                <Grid item xs={12} md={4}>
                                                    <Div sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                                                        <Typography variant="h4" color="primary">Nº {u.code}</Typography>
                                                        <Chip
                                                            size="small"
                                                            color={u.active === false ? "default" : "success"}
                                                            label={u.active === false ? "Inactivo" : "Activo"}
                                                            sx={{ml: 1}}
                                                        />
                                                    </Div>
                                                    <Div sx={{display: 'flex', alignItems: 'center', mb: 0, flexWrap: 'wrap', gap: 0.5}}>
                                                        <StoreTwoToneIcon fontSize="small" color={"primary"} />
                                                        <Typography variant="body2" color={"text.secondary"} ml={1}>{u.centre?.code}{u.centre?.name && ` - ${u.centre?.name}`}</Typography>
                                                        {_.map(_.filter(u.centres, (c) => !c.main), (c) => (
                                                            <Chip key={c.id} size="small" variant="outlined" label={c.name} sx={{height: 18, fontSize: 10}} />
                                                        ))}
                                                    </Div>
                                                    <Div sx={{display: 'flex', alignItems: 'center', mb: 0}}>
                                                        <Crop32OutlinedIcon fontSize="small" color={"primary"} />
                                                        <Typography variant="body2" color={"text.secondary"} ml={1}>{u.transport_unit1?.ppu || ""}{u.transport_unit2?.ppu ? ` | ${u.transport_unit2?.ppu}` : ""}</Typography>
                                                    </Div>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    
                                                    
                                                    {/* <Stack
                                                        direction="column"
                                                        justifyContent="start"
                                                        alignItems="left"
                                                        spacing={0.5}>

                                                        <Typography variant={"overline"}>Empresas asociadas</Typography>
                                                        { u.companies?.length <= 0 && <Typography variant={"body"}>-</Typography> }
                                                        <AvatarGroup max={3} sx={{justifyContent: 'flex-end', cursor: 'pointer'}}>
                                                            { _.map(_.uniqBy(u.companies, 'id'), (c) => {
                                                                return (
                                                                    <Tooltip title={c.name} key={`${u.id}_${c.id}`}>
                                                                        { c.logo_url ?
                                                                            <Avatar src={`${HOST}${c.logo_url}`} />
                                                                            :
                                                                            <Avatar alt={c.name}>
                                                                                {getAvatarInitials(c.name)}
                                                                            </Avatar>
                                                                        }
                                                                    </Tooltip>
                                                                )
                                                            })}
                                                        </AvatarGroup>
                                                    </Stack> */}
                                                </Grid>
                                                <Grid item xs={12} md={4} style={{height: '100%'}}>
                                                    <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
                                                        <LoadingButton
                                                            loading={recordInActivationToggle === u.id}
                                                            variant="contained"
                                                            size="small"
                                                            color={u.active === false ? "success" : "warning"}
                                                            sx={{minWidth: 130}}
                                                            onClick={() => toggleVehicleActivation(u)}>
                                                            {u.active === false ? "Activar" : "Desactivar"}
                                                        </LoadingButton>
                                                        <IconButton onClick={() => { setCurrentRecordId(u.id); setOpenRecordEditModal(true) }}>
                                                            <EditOutlinedIcon />
                                                        </IconButton>
                                                        <TrashIconButton size="medium" title="Eliminar vehículo" onClick={() => setConfirmDelete({ open: true, record: u })} />
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </React.Fragment>
                            )
                        })}
                    </List>
                </React.Fragment>
            )
        } else {
            return(
                <Typography>
                    No se encontraron datos
                </Typography>
            )
        }
    }
    
    return (
        <div>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={3}
                sx={{mb: 2}}>
                <Typography variant="h2">Vehículos</Typography>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={3}>
                    <Button variant="contained" size="small" color="success" sx={{px: 2, whiteSpace: 'nowrap'}} startIcon={<ControlPointOutlinedIcon/>} onClick={() => { setCurrentRecordId(null); setOpenRecordEditModal(true) }}>Crear</Button>                    
                    {/* Ancho fijo en pantalla grande y no solo maxWidth: el Stack le daba
                        el ancho de su contenido -unos 250px- y el placeholder se cortaba a
                        la mitad de "bodega". La fila tiene espacio de sobra al lado del
                        titulo. */}
                    <Search sx={{width: { xs: '100%', md: 360 }, maxWidth: { sm: 200, md: 360 }}}>
                        <SearchIconWrapper>
                            <SearchIcon/>
                        </SearchIconWrapper>

                        <StyledInputBase
                            placeholder="Patente, código, marca o bodega"
                            inputProps={{'aria-label': 'search'}}
                            sx={{background: 'white'}}
                            onChange={(e) => {
                                debounceSearchQueryChange(e.target.value)
                            }}
                            onKeyDown={(e) => {
                                if (e.key == 'Enter') {
                                    handleSearchQueryChange(e.target.value)
                                }
                            }}
                        />
                    </Search>
                    <FormControlLabel
                        sx={{ml: 1, whiteSpace: 'nowrap'}}
                        label={<Typography variant="body2">Ver inactivos</Typography>}
                        control={
                            <Switch
                                size="small"
                                checked={showInactive}
                                onChange={(e) => setShowInactive(e.target.checked)}
                            />
                        }
                    />
                </Stack>
            </Stack>

            <Div sx={{display: listLoading ? 'block' : 'none', textAlign: 'center', my: 5}}>
                <CircularProgress color="warning"/>
            </Div>
            <Grid container spacing={3.75}>
                <Grid item xs={12}>
                    {renderItemsList()}
                </Grid>
            </Grid>
            <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageSelect={(page) => { setCurrentPage(page); setListLoading(true)}}/>

            <ConfirmDialog
                open={confirmDelete.open}
                title="Eliminar vehículo"
                message={
                    <>
                        ¿Estás seguro que deseas eliminar el vehículo <strong>{confirmDelete.record?.code || ''}</strong>?
                        <br />
                        No se puede eliminar si tiene viajes asociados: en ese caso desactívalo.
                    </>
                }
                loading={deleting}
                onClose={() => setConfirmDelete({ open: false, record: null })}
                onConfirm={deleteRecord}
            />
        </div>
    );
};

export default UsersIndex;
